import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import JobForm, { type JobRecord } from '@/pages/dashboard/jobs/components/JobForm';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';

function JobEdit() {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<JobRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!user || !id) return;
      setLoading(true);
      const { data, error: err } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (!mounted) return;
      if (err) {
        setError(err.message);
      } else if (data) {
        setJob({
          id: data.id,
          owner_id: data.owner_id,
          title: data.title,
          company: data.company || '',
          category: data.category || '',
          contract_type: data.contract_type || '',
          salary_min: data.salary_min,
          salary_max: data.salary_max,
          city: data.city || '',
          description: data.description || '',
          requirements: data.requirements || '',
          contact_email: data.contact_email || '',
          contact_phone: data.contact_phone || '',
        });
      } else {
        setError('Offre introuvable.');
      }
      if (mounted) setLoading(false);
    };
    load();
    return () => {
      mounted = false;
    };
  }, [user, id]);

  if (!user) return null;

  return (
    <DashboardLayout>
      <div className="mb-8">
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50">Modifier l&apos;offre</h1>
        <p className="text-sm text-foreground-400 mt-1">
          Mettez à jour les informations de votre offre d&apos;emploi.
        </p>
      </div>

      <div className="max-w-3xl">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-500/10">
              <i className="ri-loader-4-line text-2xl text-secondary-400 animate-spin" />
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-sm text-red-400 mb-4">{error}</p>
            <button
              onClick={() => navigate('/dashboard/jobs')}
              className="px-5 py-2.5 border border-background-300/60 text-foreground-50 font-medium rounded-lg hover:bg-background-200/60 transition-colors text-sm whitespace-nowrap"
            >
              Retour à mes offres
            </button>
          </div>
        ) : job ? (
          <JobForm ownerId={user.id} existing={job} onSaved={() => navigate('/dashboard/jobs')} />
        ) : null}
      </div>
    </DashboardLayout>
  );
}

export default JobEdit;